/**
 * View shapes
 *
 * Model of the SHACL view configuration (shacl-view.ttl from dim-semantics)
 * the friendly entity page renders from, plus helpers on it such as turning SHACL property paths into SPARQL.
 * Parsing lives in viewShapes.parser.ts so the RDF parser is only loaded on
 * pages that need it.
 */

export const DASH_NS = 'http://datashapes.org/dash#';

export const DASH = {
  LabelViewer: `${DASH_NS}LabelViewer`,
  LiteralViewer: `${DASH_NS}LiteralViewer`,
  LangStringViewer: `${DASH_NS}LangStringViewer`,
  URIViewer: `${DASH_NS}URIViewer`,
  DetailsViewer: `${DASH_NS}DetailsViewer`,
  ValueTableViewer: `${DASH_NS}ValueTableViewer`,
  LabelRole: `${DASH_NS}LabelRole`,
  DescriptionRole: `${DASH_NS}DescriptionRole`,
  KeyInfoRole: `${DASH_NS}KeyInfoRole`,
} as const;

/** DIM's own widgets and row settings (dim-semantics src/shapes/common/dview.ttl). */
export const DVIEW_NS = 'https://data.banenor.no/shapes/dview#';

export const DVIEW = {
  LinearLocationViewer: `${DVIEW_NS}LinearLocationViewer`,
  SpotLocationViewer: `${DVIEW_NS}SpotLocationViewer`,
  AreaLocationViewer: `${DVIEW_NS}AreaLocationViewer`,
  TopologyViewer: `${DVIEW_NS}TopologyViewer`,
  BranchViewer: `${DVIEW_NS}BranchViewer`,
  // The names before the location types, read for one round so older shapes still draw
  AlongTrackViewer: `${DVIEW_NS}AlongTrackViewer`,
  TrackGraphViewer: `${DVIEW_NS}TrackGraphViewer`,
  KeyFigureViewer: `${DVIEW_NS}KeyFigureViewer`,
  KmViewer: `${DVIEW_NS}KmViewer`,
  ScaleBarViewer: `${DVIEW_NS}ScaleBarViewer`,
  SymbolViewer: `${DVIEW_NS}SymbolViewer`,
  FacilitiesViewer: `${DVIEW_NS}FacilitiesViewer`,
  LinePositionViewer: `${DVIEW_NS}LinePositionViewer`,
  ValidityViewer: `${DVIEW_NS}ValidityViewer`,
  ScaleViewer: `${DVIEW_NS}ScaleViewer`,
  YesNoViewer: `${DVIEW_NS}YesNoViewer`,
  ContentsViewer: `${DVIEW_NS}ContentsViewer`,
  TrackPeriodsViewer: `${DVIEW_NS}TrackPeriodsViewer`,
  GroupPictureViewer: `${DVIEW_NS}GroupPictureViewer`,
  SpotViewer: `${DVIEW_NS}SpotViewer`,
} as const;

/** The viewers that draw the top of the page; a node shape's dview:hero picks which of its rows do. */
export const HERO_VIEWERS = new Set<string>([
  DVIEW.LinearLocationViewer,
  DVIEW.SpotLocationViewer,
  DVIEW.AreaLocationViewer,
  DVIEW.TopologyViewer,
  DVIEW.BranchViewer,
  DVIEW.TrackGraphViewer,
  DVIEW.AlongTrackViewer,
  DVIEW.SpotViewer,
]);

export const isLinearLocationViewer = (viewer: string | null) =>
  viewer === DVIEW.LinearLocationViewer || viewer === DVIEW.AlongTrackViewer;

/** The keys of the rows the class draws at the top (dview:hero on the most specific node shape that has it), or null when none says. */
export function heroKeys(nodeShapes: ViewNodeShape[]): string[] | null {
  const shape = nodeShapes.find((node) => node.settings?.hero?.length);
  return shape ? shape.settings.hero.flatMap((value) => value.split(/\s+/)).filter(Boolean) : null;
}

/** Language-tagged strings; the untagged value is stored under ''. */
export type LangMap = Record<string, string>;

export type ShapePath =
  | { kind: 'predicate'; iri: string }
  | { kind: 'inverse'; path: ShapePath }
  | { kind: 'sequence'; steps: ShapePath[] }
  | { kind: 'alternative'; options: ShapePath[] }
  | { kind: 'zeroOrMore' | 'oneOrMore' | 'zeroOrOne'; path: ShapePath };

export interface ViewPropertyShape {
  id: string;
  path: ShapePath;
  name: LangMap;
  description: LangMap;
  /** dview:caption: a short line under the value that says what it means. */
  caption?: LangMap;
  groupId: string | null;
  order: number;
  viewer: string | null;
  role: string | null;
  hidden: boolean;
  datatypes: string[];
  nodeKind: string | null;
  classIri: string | null;
  nodeShapeId: string | null;
  minCount: number | null;
  maxCount: number | null;
  /** sh:deactivated: a projection for readers that validation ignores. */
  deactivated: boolean;
  /** dview:key, the name other rows' settings use for this row. */
  key: string | null;
  /** dview:* literals on the shape, by local name (a setting can repeat). */
  settings: Record<string, string[]>;
}

/** First value of a setting. */
export function setting(property: ViewPropertyShape, name: string): string | undefined {
  return property.settings[name]?.[0];
}

/** A setting that lists row keys, such as dview:lanes "speed gradient". */
export function settingList(property: ViewPropertyShape, name: string): string[] {
  return (property.settings[name] ?? []).flatMap((value) => value.split(/\s+/)).filter(Boolean);
}

export interface ViewNodeShape {
  id: string;
  targetClasses: string[];
  /** Classes excluded with sh:not [ sh:class ... ] (e.g. Line but not LineSection). */
  excludedClasses: string[];
  label: LangMap;
  description: LangMap;
  icon: string | null;
  /** dview: settings on the node shape (dview:overview). */
  settings: Record<string, string[]>;
  properties: ViewPropertyShape[];
}

export interface ViewGroup {
  id: string;
  label: LangMap;
  /** dview:intro: one line under the title when the group is shown as a topic. */
  intro?: LangMap;
  order: number;
  /** dview:groupViewer: the picture at the top of the group's card. */
  viewer: string | null;
  settings: Record<string, string[]>;
}

export interface ViewShapes {
  nodeShapes: ViewNodeShape[];
  nodeShapesById: Map<string, ViewNodeShape>;
  groups: Map<string, ViewGroup>;
  /** era:rinfIndex by ERA property: the RINF parameter number. */
  rinfIndex?: Map<string, string>;
}

/** Node shapes that apply to an entity with the given rdf:type values. */
export function selectNodeShapes(shapes: ViewShapes, types: string[]): ViewNodeShape[] {
  const typeSet = new Set(types);
  return shapes.nodeShapes.filter(
    (shape) =>
      shape.targetClasses.some((cls) => typeSet.has(cls)) &&
      !shape.excludedClasses.some((cls) => typeSet.has(cls))
  );
}

/** Node shape describing values of `property` (sh:node first, then sh:class). */
export function nestedNodeShape(
  shapes: ViewShapes,
  property: ViewPropertyShape
): ViewNodeShape | null {
  if (property.nodeShapeId) return shapes.nodeShapesById.get(property.nodeShapeId) ?? null;
  if (!property.classIri) return null;
  return selectNodeShapes(shapes, [property.classIri])[0] ?? null;
}

/**
 * Property shapes of several node shapes, each path shown once, in shape order.
 * dim-semantics keeps one shape set, so a path often has several property shapes
 * (the class's own, an inherited one, a validation rule), each with part of the
 * annotations. They are merged into the first shape for that path, which keeps
 * its values and takes the annotations it lacks from the others; its sh:class
 * and sh:node stay as they are, since they decide how values are shown. Shapes
 * with sh:order sort first in a node shape, and the most specific node shape
 * should come first. Shapes that share a path but differ in sh:class stay apart
 * (OP has track / has signal).
 */
export function mergeProperties(nodeShapes: ViewNodeShape[]): ViewPropertyShape[] {
  const seenIds = new Set<string>();
  const byPath = new Map<string, ViewPropertyShape[]>();
  const merged: ViewPropertyShape[] = [];
  for (const shape of nodeShapes) {
    for (const property of shape.properties) {
      if (seenIds.has(property.id)) continue;
      seenIds.add(property.id);
      const key = pathToSparql(property.path);
      const entries = byPath.get(key) ?? [];
      const index = entries.findIndex(
        (entry) => !property.classIri || !entry.classIri || entry.classIri === property.classIri
      );
      if (index === -1) {
        const entry = { ...property };
        entries.push(entry);
        byPath.set(key, entries);
        merged.push(entry);
      } else {
        Object.assign(entries[index], combineProperties(entries[index], property));
      }
    }
  }
  return merged;
}

const isEmptyLangMap = (map: LangMap) => Object.keys(map).length === 0;

/** `first` with the annotations it lacks taken from `other`, for the same path. */
function combineProperties(
  first: ViewPropertyShape,
  other: ViewPropertyShape
): ViewPropertyShape {
  // A dview:plumbing row only hides a path no other row shows
  const plumbing = (row: ViewPropertyShape) => Boolean(row.settings?.plumbing);
  if (plumbing(first) !== plumbing(other)) return plumbing(first) ? other : first;
  const hasOrder = first.order !== Number.MAX_SAFE_INTEGER;
  return {
    ...first,
    name: isEmptyLangMap(first.name) ? other.name : first.name,
    description: isEmptyLangMap(first.description) ? other.description : first.description,
    groupId: first.groupId ?? other.groupId,
    order: hasOrder ? first.order : other.order,
    viewer: first.viewer ?? other.viewer,
    role: first.role ?? other.role,
    deactivated: first.deactivated && other.deactivated,
    key: first.key ?? other.key,
    settings: { ...other.settings, ...first.settings },
    // A projection for readers (sh:deactivated) decides whether a path shows; a hidden validation row does not hide it
    hidden: first.deactivated !== other.deactivated ? (first.deactivated ? first.hidden : other.hidden) : first.hidden || other.hidden,
    datatypes: first.datatypes.length > 0 ? first.datatypes : other.datatypes,
    nodeKind: first.nodeKind ?? other.nodeKind,
    minCount: maxOf(first.minCount, other.minCount),
    maxCount: minOf(first.maxCount, other.maxCount),
  };
}

const maxOf = (a: number | null, b: number | null) =>
  a === null ? b : b === null ? a : Math.max(a, b);
const minOf = (a: number | null, b: number | null) =>
  a === null ? b : b === null ? a : Math.min(a, b);

/** SHACL property path as a SPARQL 1.1 property path. */
export function pathToSparql(path: ShapePath): string {
  switch (path.kind) {
    case 'predicate':
      return `<${path.iri}>`;
    case 'inverse':
      return `^(${pathToSparql(path.path)})`;
    case 'sequence':
      return path.steps.map((step) => `(${pathToSparql(step)})`).join('/');
    case 'alternative':
      return path.options.map((option) => `(${pathToSparql(option)})`).join('|');
    case 'zeroOrMore':
      return `(${pathToSparql(path.path)})*`;
    case 'oneOrMore':
      return `(${pathToSparql(path.path)})+`;
    case 'zeroOrOne':
      return `(${pathToSparql(path.path)})?`;
  }
}

/**
 * Forward predicates making up a path, when the path is a single predicate
 * or a sequence of forward predicates (these can be followed with DESCRIBE).
 */
export function forwardPredicates(path: ShapePath): string[] | null {
  if (path.kind === 'predicate') return [path.iri];
  if (path.kind !== 'sequence') return null;
  const predicates: string[] = [];
  for (const step of path.steps) {
    if (step.kind !== 'predicate') return null;
    predicates.push(step.iri);
  }
  return predicates;
}

/** First forward predicate of a path, used to tell which entity triples a shape covers. */
export function leadingPredicate(path: ShapePath): string | null {
  if (path.kind === 'predicate') return path.iri;
  if (path.kind === 'sequence') return leadingPredicate(path.steps[0]);
  return null;
}

const NORWEGIAN = ['nb', 'no', 'nn'];

/**
 * Best value of a language map for a UI language: exact, then language
 * family (nb/no/nn are treated as one), then untagged, then English, then any.
 */
export function pickLang(map: LangMap, uiLanguage: string): string | null {
  const base = (uiLanguage || 'en').split('-')[0].toLowerCase();
  const candidates = [base];
  if (NORWEGIAN.includes(base)) candidates.push(...NORWEGIAN.filter((lang) => lang !== base));
  candidates.push('', 'en');
  for (const lang of candidates) {
    if (map[lang]) return map[lang];
  }
  const any = Object.values(map)[0];
  return any ?? null;
}
